import { useEffect, useMemo } from 'react';

import { PreviewHeader } from '@/features/workflows/components/simple/preview-screens';
import { cn } from '@/lib/utils';

import type { SessionDetailDto, SessionStepDto } from '../../schemas/sessionSchemas';
import { formatResultDateTime } from '../../sessionDateTime';
import { formatGender } from '../../sessionFieldFormat';
import { extractError } from '../pan/pan.utils';

const STATUS_STYLE: Record<string, string> = {
  Passed: 'bg-[#DCFCE7] text-[#166534]',
  Review: 'bg-[#FEF9C3] text-[#854D0E]',
  Failed: 'bg-[#FEE2E2] text-[#991B1B]',
  Pending: 'bg-[#F3F4F6] text-[#374151]',
};

// Read-only ID Verification detail. The stored result carries ONLY the document
// that ran (normalizer emits clean keys), so we render whichever of the known
// labels are present — one label map covers all three document types.
const ID_FIELD_LABELS: Array<{ key: string; label: string }> = [
  // Voter ID
  { key: 'epic_number', label: 'EPIC Number' },
  { key: 'full_name', label: 'Full Name' },
  { key: 'relative_name', label: "Relative's Name" },
  { key: 'relation_type', label: 'Relation Type' },
  { key: 'date_of_birth', label: 'Date of Birth' },
  { key: 'gender', label: 'Gender' },
  { key: 'age', label: 'Age' },
  { key: 'house_no', label: 'House Number' },
  { key: 'assembly_constituency', label: 'Assembly Constituency' },
  { key: 'polling_station', label: 'Polling Station' },
  { key: 'district', label: 'District' },
  { key: 'state', label: 'State' },
  { key: 'pin', label: 'PIN Code' },
  { key: 'last_update', label: 'Electoral Roll Last Updated' },
  // Passport
  { key: 'passport_number', label: 'Passport Number' },
  { key: 'name_from_passport', label: 'Name (from Passport)' },
  { key: 'surname_from_passport', label: 'Surname (from Passport)' },
  { key: 'date_of_issue', label: 'Date of Issue' },
  { key: 'type_of_application', label: 'Type of Application' },
  { key: 'application_date', label: 'Application Date' },
  { key: 'name_match', label: 'Name Match' },
  { key: 'name_score', label: 'Name Match Score' },
  { key: 'passport_number_match', label: 'Passport Number Match' },
  { key: 'date_of_issue_match', label: 'Date of Issue Match' },
  // Driving Licence
  { key: 'dl_number', label: 'DL Number' },
  { key: 'dl_status', label: 'DL Status' },
  { key: 'issuing_state', label: 'State' },
  { key: 'father_or_spouse_name', label: "Father's / Spouse's Name" },
  { key: 'blood_group', label: 'Blood Group' },
  { key: 'permanent_address', label: 'Permanent Address' },
  { key: 'district', label: 'District' },
  { key: 'vehicle_class', label: 'Vehicle Class' },
  { key: 'license_issue_date', label: 'License Issue Date' },
  { key: 'non_transport_validity', label: 'Non-Transport Validity' },
  { key: 'transport_validity', label: 'Transport Validity' },
  { key: 'validity_type', label: 'Validity Type' },
  { key: 'enforcement_status', label: 'Enforcement Status' },
  { key: 'initial_issuing_office', label: 'Initial Issuing Office' },
  { key: 'last_endorsement_date', label: 'Last Endorsement Date' },
  { key: 'last_endorsed_office', label: 'Last Endorsed Office' },
  { key: 'endorsement_reason', label: 'Endorsement Reason' },
  { key: 'hazardous_valid_till', label: 'Hazardous Valid Till' },
  { key: 'hill_valid_till', label: 'Hill Valid Till' },
  // Shared risk / metadata
  { key: 'age_restriction_flag', label: 'Age Restriction Flag' },
  { key: 'duplicate_epic_flag', label: 'Duplicate EPIC Flag' },
  { key: 'duplicate_dl_flag', label: 'Duplicate DL Flag' },
  { key: 'verification_status', label: 'Status' },
  { key: 'verification_attempts', label: 'Verification Attempts' },
  { key: 'verified_at', label: 'Verified At' },
  { key: 'method_used', label: 'Method Used' },
];

export function IdVerificationDetail({
  step,
  formId,
  onPendingChange,
  onReadyChange,
}: {
  session: SessionDetailDto;
  step: SessionStepDto;
  formId: string;
  onPendingChange: (pending: boolean) => void;
  onReadyChange: (ready: boolean) => void;
}) {
  useEffect(() => {
    onPendingChange(false);
    onReadyChange(false);
  }, [onPendingChange, onReadyChange]);

  const error = extractError(step.resultJson);
  const claims = useMemo(() => extractClaims(step.resultJson), [step.resultJson]);

  return (
    <form id={formId} onSubmit={(e) => e.preventDefault()} className="flex flex-col gap-4">
      <PreviewHeader title="ID Verification" subtitle="Document verification result" />

      <div className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2.5">
        <span className="text-sm text-[#6B7280]">Status</span>
        <span
          className={cn(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            STATUS_STYLE[step.status] ?? STATUS_STYLE.Pending,
          )}
        >
          {step.status}
        </span>
      </div>

      {claims.length > 0 && (
        <div className="flex flex-col divide-y divide-[#F3F4F6] rounded-lg border border-[#E5E7EB]">
          {claims.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-[#6B7280]">{label}</span>
              <span className="text-sm font-medium text-[#111827]">{value}</span>
            </div>
          ))}
        </div>
      )}

      {error && step.status === 'Failed' && <p className="text-xs text-[#BF3636]">{error}</p>}
    </form>
  );
}

// Voter ID relation-type codes returned by the gateway (rlnType). Expanded for
// display; any unknown value is shown verbatim as it comes from SignCare.
const RELATION_TYPE_LABELS: Record<string, string> = {
  F: 'Father',
  M: 'Mother',
  H: 'Husband',
  W: 'Wife',
};

function formatValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key === 'relation_type') {
    const raw = String(value).trim();
    return RELATION_TYPE_LABELS[raw.toUpperCase()] ?? raw;
  }
  if (key === 'name_score') {
    const n = Number(value);
    if (Number.isNaN(n)) return String(value);
    // Passport nameScore is a 0-1 fraction from the gateway (1.0 = 100%); values
    // already >1 are treated as an existing percentage (covers older sessions).
    const pct = n <= 1 ? n * 100 : n;
    return `${pct.toFixed(0)}%`;
  }
  if (key === 'gender') return formatGender(value);
  if (key === 'verified_at') {
    return formatResultDateTime(value);
  }
  return String(value);
}

function extractClaims(resultJson: string | null): [string, string][] {
  if (!resultJson) return [];
  let parsed: Record<string, unknown> | null = null;
  try {
    const p = JSON.parse(resultJson);
    parsed = p && typeof p === 'object' ? (p as Record<string, unknown>) : null;
  } catch {
    return [];
  }
  if (!parsed) return [];
  const out: [string, string][] = [];
  for (const { key, label } of ID_FIELD_LABELS) {
    if (key in parsed && parsed[key] !== null && parsed[key] !== '') {
      out.push([label, formatValue(key, parsed[key])]);
    }
  }
  return out;
}
